import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { useOrganization } from '../../../utils/OrganizationContext';
import { useAuth } from '../../../utils/Auth';
import API from '../../../API';
import PlanSelector from '../PlanSelector/index.js';
import Avatar from '../../../components/Avatar';
import FileInput from '../../../components/FileInput';
import { useImageOptimizer } from '../../../utils/hooks/useImageOptimizer';
import styles from './Settings.module.css';

// Helper function to extract billing display info
const getBillingInfo = (currentOrg, config, orgMetadata) => {
	// Extract plan ID from org
	const planId = currentOrg?.plan?.id ?? currentOrg?.plan?.plan_id ?? currentOrg?.plan_id ?? null;
	const isFree = !planId || planId === 0 || planId === '0' || planId === 'free';

	// Free plan case - no need to check stripe metadata
	if (isFree) {
		return {
			planName: 'Free',
			priceLabel: '$0/month',
			billingLabel: 'Free',
			nextBillingLabel: 'N/A'
		};
	}

	// Paid plan case - get plan config
	const planConfig = config?.plans?.[planId];
	if (!planConfig) {
		return {
			planName: 'Unknown',
			priceLabel: 'Unknown',
			billingLabel: 'Unknown',
			nextBillingLabel: 'Unknown'
		};
	}

	// Determine billing cycle from stripe metadata
	const billingCycleRaw = orgMetadata?.stripe?.billing_cycle;
	const billingCycle = billingCycleRaw === 'year' ? 'annual' : 'monthly';

	// Calculate price per month for selected billing cycle
	const cost = planConfig.stripe_prices?.[billingCycle]?.cost ?? null;
	const priceLabel = cost ? `$${cost}/month` : 'Unknown';

	// Calculate next billing date from next_billing_date
	const lastBillingDate = (() => {
		const raw = orgMetadata?.stripe?.next_billing_date;
		if (!raw) return null;
		if (typeof raw === 'number') return new Date(raw);
		if (typeof raw?.toDate === 'function') return raw.toDate();
		if (typeof raw?.seconds === 'number') return new Date(raw.seconds * 1000);
		return null;
	})();

	let nextBillingLabel = 'Unknown';
	if (lastBillingDate) {
		nextBillingLabel = lastBillingDate.toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric'
		});
	}

	return {
		planName: planConfig.name || 'Unknown',
		priceLabel,
		billingLabel: billingCycle === 'annual' ? 'Annual' : 'Monthly',
		nextBillingLabel
	};
};

export default function Settings() {
	const navigate = useNavigate();
	const location = useLocation();
	const { activeOrgId, config, userObj, orgObj } = useAuth();
	const { organizations, currentUserOrgData, orgMembers, orgMetadata } = useOrganization();
	const processFile = useImageOptimizer();

	// Plan selector state
	const [showPlanSelector, setShowPlanSelector] = useState(false);
	const [selectedPlanId, setSelectedPlanId] = useState(null);
	const [billingCycle, setBillingCycle] = useState(null);
	const [checkoutRequired, setCheckoutRequired] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');

	// Org name edit state
	const [isEditingOrgName, setIsEditingOrgName] = useState(false);
	const [orgName, setOrgName] = useState('');
	const MAX_ORG_NAME_LENGTH = 30;
	const orgNameSectionRef = useRef(null);

	// Avatar state
	const [avatarUrl, setAvatarUrl] = useState('');
	const [uploadingAvatar, setUploadingAvatar] = useState(false);
	const [avatarError, setAvatarError] = useState('');

	const isPrimary = useMemo(() => currentUserOrgData?.is_primary, [currentUserOrgData?.is_primary]);
	const currentOrg = useMemo(() => organizations?.find(org => org.id === activeOrgId), [organizations, activeOrgId]);
	const otherMembers = useMemo(() => orgMembers.filter(m => m.uid !== currentUserOrgData?.uid), [orgMembers, currentUserOrgData]);

	// Check if current user has permission to update subscription (admin role or primary owner)
	const canUpdateSubscription = useMemo(() => {
		return currentUserOrgData?.role === 'admin' || currentUserOrgData?.is_primary === true;
	}, [currentUserOrgData?.role, currentUserOrgData?.is_primary]);

	// Check if org has active subscription (both customer_id and sub_id must exist)
	const hasActiveSubscription = useMemo(() => {
		return !!orgMetadata?.stripe?.customer_id && !!orgMetadata?.stripe?.sub_id;
	}, [orgMetadata?.stripe?.customer_id, orgMetadata?.stripe?.sub_id]);

	const billingState = useMemo(
		() => getBillingInfo(currentOrg, config, orgMetadata),
		[currentOrg, config, orgMetadata]
	);

	// Handle Stripe checkout/portal return
	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const success = searchParams.get('success');
		const canceled = searchParams.get('canceled');
		const returnContext = searchParams.get('return_url_context');

		// Check if this is a return from Stripe checkout for settings context
		if (returnContext === 'settings' && success === 'true' && canceled !== 'true') {
			setError('');
			setSuccess('Subscription updated successfully!');
			setShowPlanSelector(false);
			setSelectedPlanId(null);
			setBillingCycle(null);
			setCheckoutRequired(false);

			// Clear URL params
			navigate('/organizations/settings', { replace: true });
		} else if (returnContext === 'settings' && canceled === 'true') {
			setError('Payment was canceled. Your subscription was not updated.');
			setShowPlanSelector(false);
			setSelectedPlanId(null);
			setBillingCycle(null);
			setCheckoutRequired(false);

			// Clear URL params
			navigate('/organizations/settings', { replace: true });
		}
	}, [location.search, navigate]);

	const handleSelectPlan = useCallback(({ planId, billingCycle }) => {
		setSelectedPlanId(planId);
		setBillingCycle(billingCycle);
	}, []);

	const handleCheckoutOrPortal = useCallback(async (event) => {
		event.preventDefault();

		if (!canUpdateSubscription) {
			setError('You do not have permission to update the subscription.');
			return;
		}

		if (hasActiveSubscription && !checkoutRequired) {
			// Redirect to Stripe customer portal
			setSubmitting(true);
			setError('');

			try {
				const customerId = orgMetadata?.stripe?.customer_id;
				if (!customerId) {
					throw new Error('No customer ID found');
				}

				const portalResult = await API.createStripeCustomerSession({
					customer_id: customerId,
          return_url_context: "orgSettings",
				});

				if (portalResult?.data?.url) {
					window.location.href = portalResult.data.url;
				} else if (portalResult?.data?.type === 'checkout_required') {
					setCheckoutRequired(true);
					setShowPlanSelector(true);
					setSubmitting(false);
				} else {
					throw new Error('No portal URL returned from Stripe');
				}
			} catch (err) {
				console.error("Error creating Stripe portal session:", err);
				setError(err?.response?.data?.message || err?.message || "Could not access billing portal, please try again");
				setSubmitting(false);
			}
		} else {
			// Show plan selector for new subscription
			if (!selectedPlanId || !billingCycle) {
				setError('Please select a plan and billing cycle.');
				return;
			}

			setSubmitting(true);
			setError('');

			try {
				// Get the Stripe price_id from the selected plan
				const selectedPlan = config?.plans?.[selectedPlanId];
				if (!selectedPlan) {
					throw new Error('Selected plan not found');
				}

				const stripePriceId = selectedPlan?.stripe_prices?.[billingCycle]?.price;
				if (!stripePriceId) {
					throw new Error('Price ID not found for selected plan and billing cycle');
				}

				// Create Stripe checkout session
				const checkoutData = {
					org_id: activeOrgId,
					org_name: currentOrg?.name,
					customer_id: checkoutRequired ? (orgMetadata?.stripe?.customer_id || '') : '',
					price_id: stripePriceId,
					uid: userObj?.uid,
					trial: false,
					return_url_context: 'settings'
				};

				const checkoutResult = await API.createStripeCheckout(checkoutData);
				console.log('Stripe checkout result:', checkoutResult);

				// Redirect to Stripe checkout
				if (checkoutResult?.data?.url) {
					window.location.href = checkoutResult.data.url;
				} else {
					throw new Error('No checkout URL returned from Stripe');
				}
			} catch (err) {
				console.error("Error creating checkout session:", err);
				setError(err?.response?.data?.message || err?.message || "Could not proceed with subscription update, please try again");
				setSubmitting(false);
			}
		}
	}, [canUpdateSubscription, hasActiveSubscription, checkoutRequired, selectedPlanId, billingCycle, orgMetadata?.stripe?.customer_id, config?.plans, activeOrgId, currentOrg?.name, userObj?.uid]);

	const handleRoleChange = (uid, newRole) => {
		// TODO: update logic below for 'newRole' to go from admin to something else
		console.log(`Changing user ${uid} to ${newRole}`)
	};

	const handleTransferOwnership = () => {
		const confirm = window.confirm("Are you sure? You will lose ownership privileges.");
		if (confirm) {
			// TODO: Api call to transfer
			// potentially wait to change current ownership until new owner accepts/updates their stripe account?
		}
	};

	const handleUpdateSubscription = useCallback(() => {
		if (!canUpdateSubscription) {
			setError('You do not have permission to update the subscription.');
			return;
		}

		if (hasActiveSubscription) {
			if (checkoutRequired) {
				setShowPlanSelector(true);
				setError('');
			} else {
				// Go directly to portal
				handleCheckoutOrPortal({ preventDefault: () => {} });
			}
		} else {
			// Show plan selector for new subscription
			setShowPlanSelector(true);
			setError('');
		}
	}, [canUpdateSubscription, hasActiveSubscription, checkoutRequired, handleCheckoutOrPortal]);

	const handleBackFromPlanSelector = useCallback(() => {
		setShowPlanSelector(false);
		setSelectedPlanId(null);
		setBillingCycle(null);
		setCheckoutRequired(false);
		setError('');
	}, []);

	// Initialize org name from currentOrg
	useEffect(() => {
		if (currentOrg) {
			setOrgName(currentOrg?.name ?? '');
		}
	}, [currentOrg]);

	// Initialize avatar URL from orgMetadata
	useEffect(() => {
		if (orgObj?.metadata?.avatar_image_url) {
			setAvatarUrl(orgObj?.metadata?.avatar_image_url);
		}
	}, [orgObj?.metadata?.avatar_image_url]);

	// Click outside to close org name editing
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (orgNameSectionRef.current && !orgNameSectionRef.current.contains(event.target)) {
				setIsEditingOrgName(false);
			}
		};

		if (isEditingOrgName) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isEditingOrgName]);

	// Handle updating org
	const handleUpdateOrg = useCallback(async (updatedName) => {
		try {
			const trimmedName = updatedName.trim();
			const isPersonalOrg = currentOrg?.metadata?.type === 'personal';

			if (!isPersonalOrg && trimmedName === '') {
				setOrgName(currentOrg?.name ?? '');
				return;
			}

			if (trimmedName === '' || trimmedName !== currentOrg?.name) {
				await API.updateOrg(activeOrgId, { name: trimmedName });
			}
		} catch (error) {
			console.error('Error updating organization:', error.message);
			// Reset to original name on error
			setOrgName(currentOrg?.name || '');
		}
	}, [activeOrgId, currentOrg]);

	// Debounced org name update
	const debouncedOrgUpdate = useCallback(
		debounce((name) => {
			handleUpdateOrg(name);
		}, 1000),
		[handleUpdateOrg]
	);

	// Auto-save org name on change
	useEffect(() => {
		const isPersonalOrg = currentOrg?.metadata?.type === 'personal';
		if (activeOrgId && (orgName === '' || orgName) && orgName !== currentOrg?.name) {
			const trimmedName = orgName.trim();
			const isValidName = trimmedName.length <= MAX_ORG_NAME_LENGTH;

			if (isValidName && (isPersonalOrg || trimmedName !== '')) {
				debouncedOrgUpdate(trimmedName);
			}
		}

		return () => debouncedOrgUpdate.cancel();
	}, [orgName, currentOrg, activeOrgId, debouncedOrgUpdate]);

	const handleStartEditOrgName = useCallback(() => {
		setIsEditingOrgName(true);
	}, []);

	// Handle avatar file selection
	const handleAvatarFileSelected = useCallback(async (files) => {
		if (!files || files.length === 0) return;
		if (!canUpdateSubscription) {
			setAvatarError('You do not have permission to update the avatar.');
			return;
		}

		setUploadingAvatar(true);
		setAvatarError('');

		try {
			const file = files[0];
			const id = Date.now();

			// Process the image through the optimizer
			const processedResult = await processFile(file, 'image/jpeg,image/png,image/webp', id, 'avatar');
			
			// Create FormData for upload
			const formData = new FormData();
			
			if (processedResult.files && processedResult.files.length > 0) {
				for (const processedFile of processedResult.files) {
					formData.append('files', processedFile, `${id}.webp`);
				}
			} else {
				formData.append('files', file);
			}

			// Upload avatar to server
			const response = await API.uploadOrgAvatar(activeOrgId, formData);
			
			if (response?.data?.url) {
				// Update local state immediately
				setAvatarUrl(response.data.url);
				setSuccess('Avatar updated successfully!');
				setTimeout(() => setSuccess(''), 3000);
			}
		} catch (err) {
			console.error('Error uploading avatar:', err);
			setAvatarError(err?.response?.data?.message || err?.message || 'Failed to upload avatar. Please try again.');
		} finally {
			setUploadingAvatar(false);
		}
	}, [activeOrgId, canUpdateSubscription, processFile, orgMetadata]);

	// Handle avatar removal
	const handleRemoveAvatar = useCallback(async () => {
		if (!canUpdateSubscription) {
			setAvatarError('You do not have permission to remove the avatar.');
			return;
		}

		setUploadingAvatar(true);
		setAvatarError('');

		try {
			// Call the dedicated remove avatar endpoint
			await API.removeOrgAvatar(activeOrgId);
			
			setAvatarUrl('');
			setSuccess('Avatar removed successfully!');
			setTimeout(() => setSuccess(''), 3000);
		} catch (err) {
			console.error('Error removing avatar:', err);
			setAvatarError('Failed to remove avatar. Please try again.');
		} finally {
			setUploadingAvatar(false);
		}
	}, [activeOrgId, canUpdateSubscription]);

	// styles role badge
	const handleRoleStyle = (role) => {
		switch(role) {
			case 'viewer':
				return { borderColor: 'rgb(249,105,14)', backgroundColor: 'rgba(249,105,14,.1)', color: 'rgb(249,105,14)' }
			case 'manager':
				return { borderColor: 'rgb(38,166,91)', backgroundColor: 'rgba(38,166,91,.1)', color: 'rgb(38,166,91)' }
			case 'admin':
				return { borderColor: 'rgb(9,99,219)', backgroundColor: 'rgba(9,99,219,.1)', color: 'rgb(9,99,219)' }
			case 'editor':
				return { borderColor: 'rgb(140,20,252)', backgroundColor: 'rgba(140,20,252,.1)', color: 'rgb(140,20,252)' }
		}
	}

	return (
		<div className={styles.wrapper}>
			<div className={styles.container}>
				{!showPlanSelector ? (
					<>
						<div className={styles.header}>
							<div className={styles.headerTop}>
								<button className={styles.backButton} onClick={() => navigate('/organizations')}>
									<i className='fas fa-chevron-left' />
								</button>
								<h1 className={styles.title}>Organization Settings</h1>
							</div>
							{currentOrg?.name && (
								<div className={styles.headerBottom}>
									<p className={styles.orgName}>Organization: <strong>{currentOrg.name}</strong></p>
								</div>
							)}
						</div>
						<div className={styles.content}>
							<div className={styles.twoColumnLayout}>
								<section className={styles.sectionCard}>
									<h3>Subscription & Billing</h3>
									<p>Manage your organization's plan and payment methods.</p>
									<div className={styles.billingInfo}>
										<span>Current Plan: <strong>{billingState.planName}{billingState.billingLabel !== 'Unknown' ? ` (${billingState.billingLabel})` : ''}</strong></span>
										<span>Price: <strong>{billingState.priceLabel}</strong></span>
										<span>Billing Cycle: <strong>{billingState.billingLabel}</strong></span>
										<span>Next Billing Date: <strong>{billingState.nextBillingLabel}</strong></span>
										<button 
											className={styles.primaryButton} 
											onClick={handleUpdateSubscription}
											disabled={!canUpdateSubscription}
											title={canUpdateSubscription ? '' : 'You do not have permission to update the subscription'}
										>
											Update Subscription
										</button>
									</div>
									{error && <div className={styles.error}>{error}</div>}
								</section>

								<section className={styles.sectionCard}>
							    <h3>Branding</h3>
									<p>Manage your organization name and avatar.</p>
									<div className={styles.brandingContent} ref={orgNameSectionRef}>
										{/* Avatar Section */}
										<div className={styles.avatarSection}>
											<label className={styles.fieldLabel}>Organization Avatar</label>
											<div className={styles.avatarContainer}>
												<Avatar 
													src={avatarUrl} 
													orgName={orgName} 
													size="medium"
													alt={`${orgName} avatar`}
												/>
												<div className={styles.avatarActions}>
													<FileInput
														onFileSelect={handleAvatarFileSelected}
														acceptedFileTypes="image/jpeg,image/png,image/webp"
														multiple={false}
														label={uploadingAvatar ? 'Uploading...' : 'Upload Avatar'}
														id="avatar-upload"
														name="avatar"
														disabled={!canUpdateSubscription || uploadingAvatar}
													/>
													{avatarUrl && (
														<button
															className={styles.secondaryButton}
															onClick={handleRemoveAvatar}
															disabled={!canUpdateSubscription || uploadingAvatar}
															type="button"
														>
															Remove Avatar
														</button>
													)}
												</div>
											</div>
											{avatarError && <div className={styles.error}>{avatarError}</div>}
										</div>

										{/* Organization Name Section */}
										<label className={styles.fieldLabel}>Organization Name</label>
										{!isEditingOrgName ? (
											<div 
												className={styles.fieldDisplay} 
												onClick={handleStartEditOrgName}
											>
												<span>{orgName || 'Unnamed Organization'}</span>
												{canUpdateSubscription && <i className='fas fa-edit' />}
											</div>
										) : (
											<div className={styles.inputWithIcon}>
												<input
													type='text'
													className={styles.fieldInput}
													placeholder='Enter organization name'
													value={orgName}
													onChange={(e) => setOrgName(e.target.value.slice(0, MAX_ORG_NAME_LENGTH))}
													disabled={!canUpdateSubscription}
													maxLength={MAX_ORG_NAME_LENGTH}
													autoFocus
												/>
												<i className='fas fa-edit' />
											</div>
										)}
										<div className={styles.charCount}>
											{orgName.length}/{MAX_ORG_NAME_LENGTH}
										</div>
									</div>
								</section>
							</div>

							{success && <div className={styles.success}>{success}</div>}

							<section className={styles.sectionCard}>
								<h3>Manage Administrative Roles</h3>
								<p>Assign admin roles to members to help manage the organization.</p>
								<div className={styles.memberList}>
									{otherMembers?.map(member => (
										<div key={member.uid} className={styles.memberItem}>
											<div className={styles.memberInfo}>
												<p className={styles.memberName}>{member.email}</p>
												<span className={styles.roleBadge} style={handleRoleStyle(member.role)}>{member.role.charAt(0).toUpperCase() + member.role.slice(1)}</span>
											</div>
											<div className={styles.actions}>
												{member.role === 'admin' ? (
													<button className={styles.secondaryButton} onClick={() => handleRoleChange(member.uid, 'member')}>Remove Admin</button>
												) : (
													<button className={styles.secondaryButton} onClick={() => handleRoleChange(member.uid, 'admin')}>Make Admin</button>
												)}
											</div>
										</div>
									))}
								</div>
							</section>
							{isPrimary && (
								<section className={`${styles.sectionCard} ${styles.dangerZone}`}>
									<h3>Transfer Ownership</h3>
									<p>Transfer this organization to another user. This action cannot be undone.</p>
									<div className={styles.transferRow}>
										<select className={styles.selectInput}>
											<option>Select a member...</option>
											{otherMembers?.map(m => <option key={m.uid} value={m.uid}>{m.email}</option>)}
										</select>
										<button className={styles.dangerButton} onClick={handleTransferOwnership}>Transfer Ownership</button>
									</div>
								</section>
							)}
						</div>
					</>
				) : (
					<>
						<div className={styles.header}>
							<div className={styles.headerTop}>
								<button className={styles.backButton} onClick={handleBackFromPlanSelector}>
									<i className='fas fa-chevron-left' />
								</button>
								<h1 className={styles.title}>Update Subscription</h1>
							</div>
						</div>
						<div className={styles.content}>
							<form onSubmit={handleCheckoutOrPortal}>
								<PlanSelector onSelectPlan={handleSelectPlan} />
								
								<div className={styles.actionsContainer}>
									<button 
										className={styles.primaryButton} 
										type="submit" 
										disabled={submitting || (!hasActiveSubscription && !selectedPlanId)}
									>
										{submitting ? (hasActiveSubscription ? 'Redirecting...' : 'Processing...') : (hasActiveSubscription ? 'Go to Billing Portal' : 'Upgrade Plan')}
									</button>
									<button 
										type="button"
										className={styles.secondaryButton}
										onClick={handleBackFromPlanSelector}
										disabled={submitting}
									>
										← Back to Settings
									</button>
								</div>

								{error && <div className={styles.error}>{error}</div>}
								{success && <div className={styles.success}>{success}</div>}
							</form>
						</div>
					</>
				)}
			</div>
		</div>
	);
}